import type { ApiError, HttpError } from "@mimber/schema"

/**
 * Extracts the structured `ApiError` from an error thrown by the API client,
 * if present.
 *
 * The Axios interceptor attaches the response body at `error.response.data`,
 * which follows the standard `{ data: null, meta: { error, ... } }` envelope.
 * Returns `undefined` for errors that don't carry this envelope (e.g. network
 * errors, or errors not originating from the API).
 */
export const extractApiError = (error: unknown): ApiError | undefined => {
  if (!error) return undefined

  return (error as HttpError)?.response?.data?.meta?.error ?? undefined
}

/**
 * Extracts a user-facing error message from an error thrown by the API client.
 * Prefers the API-provided message (`error.response.data.meta.error.message`)
 * over the generic `Error.message`, falling back to `fallback` if neither is
 * available.
 */
export const extractApiErrorMessage = (
  error: unknown,
  fallback?: string
): string | undefined => {
  if (!error) return fallback

  const apiErrorMessage = extractApiError(error)?.message
  if (apiErrorMessage) return apiErrorMessage

  if (error instanceof Error && error.message) return error.message

  return fallback
}
